import React, { useEffect, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Footer from "../components/Footer";
import Header from "../components/Header";
import { getPublicService } from "../api";
import { SERVICES } from "../lib/services";
import { CheckCircle2, ArrowRight, Loader2, ChevronDown, Euro, Phone } from "lucide-react";
import { log } from "../lib/log";

const Section = ({ eyebrow, title, children, className = "" }) => (
  <section className={`max-w-6xl mx-auto px-5 py-14 md:py-20 ${className}`}>
    {eyebrow && <p className="text-xs font-semibold tracking-[0.2em] text-brand uppercase mb-3">{eyebrow}</p>}
    {title && <h2 className="font-heading font-bold text-base md:text-lg text-neutral-900 max-w-2xl">{title}</h2>}
    {children}
  </section>
);

const FaqItem = ({ f, i }) => {
  const [open, setOpen] = useState(i === 0);
  return (
    <div className="border border-neutral-200 rounded-xl" data-testid={`service-faq-${i}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between gap-3 text-left px-5 py-4 font-semibold text-neutral-900">{f.q} <ChevronDown size={16} className={`shrink-0 transition-transform ${open ? "rotate-180" : ""}`} /></button>
      {open && <p className="px-5 pb-4 text-sm text-neutral-600">{f.a}</p>}
    </div>
  );
};

export default function ServicePage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    setPage(null); setError(false);
    getPublicService(slug).then((r) => setPage(r.data)).catch((e) => { log.error("service page:", e); setError(true); });
  }, [slug]);

  if (error) return <Navigate to="/" replace />;
  if (page?.external) return <Navigate to={page.external} replace />;
  if (!page) return <div className="min-h-screen flex items-center justify-center text-neutral-400"><Loader2 className="animate-spin" /></div>;
  const svc = SERVICES[page.service] || SERVICES.clean;
  const Icon = svc.icon;

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Helmet><title>{page.seo_title || `${page.title} | PTITSERVICES-PRO`}</title><meta name="description" content={page.seo_description || page.tagline} /><link rel="canonical" href={`https://ptitservices-pro.com/services/${slug}`} /></Helmet>
      <Header solid />
      <main className="flex-1" data-testid="service-page">
        <section className="relative overflow-hidden bg-neutral-950 text-white">
          {page.hero_image && <img src={page.hero_image} alt={page.title} className="absolute inset-0 w-full h-full object-cover opacity-40" />}
          <div className="relative max-w-6xl mx-auto px-5 py-20 md:py-28">
            <p className="text-xs font-semibold tracking-[0.2em] text-brand uppercase mb-4 flex items-center gap-2"><Icon size={14} /> {svc.label}</p>
            <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-tight max-w-3xl" data-testid="service-title">{page.title}</h1>
            <p className="text-neutral-300 mt-6 text-base md:text-lg max-w-2xl">{page.tagline}</p>
            <div className="flex flex-wrap gap-3 mt-8">
              <a href="/#devis" className="inline-flex items-center gap-2 bg-brand hover:bg-brand-dark text-white font-semibold px-5 py-3 rounded-md" data-testid="service-cta-hero">Demander un devis <ArrowRight size={16} /></a>
              <a href="/#contact" className="inline-flex items-center gap-2 border border-white/30 hover:border-white text-white font-semibold px-5 py-3 rounded-md"><Phone size={16} /> Être rappelé</a>
            </div>
          </div>
        </section>

        <Section eyebrow="Le métier" title={`${page.title} : notre façon de travailler`}>
          <div className="mt-5 text-neutral-700 leading-relaxed max-w-3xl space-y-4">{String(page.metier || "").split(/\n\n+/).map((p, i) => <p key={`${i}-${p.slice(0, 20)}`}>{p}</p>)}</div>
        </Section>

        {page.deroule?.length > 0 && (
          <section className="bg-neutral-50 border-y border-neutral-200">
            <Section eyebrow="Comment ça se passe" title="De la visite au suivi, en 4 étapes" className="py-14">
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-8" data-testid="service-steps">
                {page.deroule.map((s, i) => (
                  <div key={s.title} className="bg-white border border-neutral-200 rounded-2xl p-5">
                    <span className="font-heading font-extrabold text-3xl text-brand/30">{String(i + 1).padStart(2, "0")}</span>
                    <h3 className="font-semibold text-neutral-900 mt-2">{s.title}</h3>
                    <p className="text-sm text-neutral-600 mt-1.5">{s.text}</p>
                  </div>
                ))}
              </div>
            </Section>
          </section>
        )}

        <Section eyebrow="Pourquoi nous" title="Les avantages PTITSERVICES-PRO">
          <div className="grid md:grid-cols-[1.2fr_1fr] gap-10 mt-8">
            <ul className="grid sm:grid-cols-2 gap-3" data-testid="service-avantages">
              {(page.avantages || []).map((a, i) => <li key={`${i}-${a}`} className="flex items-start gap-2 text-neutral-800 bg-white border border-neutral-200 rounded-xl px-4 py-3"><CheckCircle2 size={17} className="text-brand mt-0.5 shrink-0" /> {a}</li>)}
            </ul>
            <div>
              <p className="font-heading font-bold text-neutral-900">Pour qui ?</p>
              <div className="flex flex-wrap gap-2 mt-3" data-testid="service-cibles">{(page.pour_qui || []).map((c, i) => <span key={`${i}-${c}`} className="text-sm text-neutral-700 bg-neutral-100 px-3 py-1.5 rounded-md">{c}</span>)}</div>
              {page.inclus?.length > 0 && (<>
                <p className="font-heading font-bold text-neutral-900 mt-8">Ce qui est inclus</p>
                <ul className="mt-3 space-y-1.5" data-testid="service-inclus">{page.inclus.map((x, i) => <li key={`${i}-${x}`} className="text-sm text-neutral-700 flex items-start gap-2"><span className="text-brand mt-1">•</span> {x}</li>)}</ul>
              </>)}
            </div>
          </div>
        </Section>

        {page.tarif && (
          <section className="max-w-6xl mx-auto px-5">
            <div className="border border-brand/30 bg-brand/5 rounded-2xl p-6 flex items-start gap-3" data-testid="service-tarif"><Euro size={20} className="text-brand mt-0.5 shrink-0" /><div><p className="font-semibold text-neutral-900">Tarification</p><p className="text-sm text-neutral-700 mt-1">{page.tarif}</p></div></div>
          </section>
        )}

        {page.faq?.length > 0 && (
          <Section eyebrow="Questions fréquentes" title={`Tout savoir sur : ${page.title.toLowerCase()}`}>
            <div className="space-y-3 mt-8 max-w-3xl" data-testid="service-faq">{page.faq.map((f, i) => <FaqItem key={f.q} f={f} i={i} />)}</div>
          </Section>
        )}

        <section className="max-w-6xl mx-auto px-5 pb-16 md:pb-24">
          <div className="bg-neutral-950 text-white rounded-2xl p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div><p className="font-heading font-bold text-xl">Parlons de votre site</p><p className="text-neutral-400 text-sm mt-1">Devis gratuit sous 24 h, visite sur place offerte dans tout le Grand Nord de la France.</p></div>
            <a href="/#devis" className="inline-flex items-center gap-2 bg-brand hover:bg-brand-dark text-white font-semibold px-5 py-3 rounded-md" data-testid="service-cta-bottom">Demander un devis <ArrowRight size={16} /></a>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
